export const isReducedMotion = window.matchMedia(
  '(prefers-reduced-motion: reduce)',
).matches

export function isMobileView() {
  const mobileBreakpoint = getComputedStyle(document.documentElement)
    .getPropertyValue('--dds-breakpoints-small')
    .trim()

  const isMobileView = window.matchMedia(
    `(max-width: ${mobileBreakpoint})`,
  ).matches

  return isMobileView
}

export function setFromParentSize(element: HTMLElement) {
  const size = element.closest('dap-ds-card')?.getAttribute('size')
  element.setAttribute('size', size ?? 'sm')
}

export function numberRange(start: number, end: number): number[] {
  return Array.from({ length: end - start + 1 }, (_, i) => start + i)
}

export const JSONAttributeParser = (value: string | null) => {
  let convertedValue = {}
  try {
    convertedValue = JSON.parse(value ?? '')
  } catch (error) {
    console.error('Incorrect Json string data', {
      data: value,
    })
  }

  return convertedValue
}

/**
 * Normalizes a string value that might be null, undefined, or the string "null"/"undefined".
 * This is useful for handling values from React/Next.js object spreads where null props
 * can be serialized as the string "null".
 *
 * @param value - The value to normalize
 * @returns An empty string if the value is null, undefined, or the string "null"/"undefined", otherwise the value
 *
 * @example
 * ```typescript
 * protected updated(changedProperties: Map<PropertyKey, unknown>): void {
 *   super.updated(changedProperties)
 *   if (changedProperties.has('title')) {
 *     this.title = normalizeStringProperty(this.title)
 *   }
 * }
 * ```
 */
export function normalizeStringProperty(
  value: string | null | undefined,
): string {
  if (
    value === null ||
    value === undefined ||
    value === 'null' ||
    value === 'undefined'
  ) {
    return ''
  }
  return value
}

/**
 * Lit property converter that handles null/undefined values from React/Next.js object spreads.
 *
 * When React serializes null props (e.g., from object spreads like {...props} where props.title = null),
 * it can become the string "null" in HTML attributes. This converter normalizes these cases.
 *
 * @example
 * ```typescript
 * @property({
 *   converter: stringPropertyConverter
 * })
 * title: string
 * ```
 *
 * This prevents the string "null" from appearing in the rendered output when:
 * - Props come from object spreads with null values
 * - React/Next.js serializes null as the string "null"
 * - Properties are set directly (bypassing attribute conversion)
 *
 * Note: For direct property assignments (e.g., `element.title = null`), also use
 * `normalizeStringProperty` in the `updated` lifecycle method.
 */
export const stringPropertyConverter = {
  fromAttribute: (value: string | null): string => {
    return normalizeStringProperty(value)
  },
  toAttribute: (value: string | null | undefined): string | null => {
    // Prevent reflecting null/undefined/empty values back to attributes
    // This avoids setting title="null" or title="" in the DOM
    if (!value || value === 'null' || value === 'undefined') {
      return null // null removes the attribute
    }
    return value
  },
}

/**
 * Finds the nearest scrollable ancestor of an element, crossing shadow DOM and
 * slot boundaries. Returns null if none is found before the document root.
 */
export function getScrollParent(element: Element): HTMLElement | null {
  const getParent = (node: Node): Node | null => {
    // Slotted content lives visually inside the slot, so continue up through it.
    if (node instanceof Element && node.assignedSlot) {
      return node.assignedSlot
    }
    const parent = node.parentNode
    // Cross a shadow root boundary via its host element.
    if (parent instanceof ShadowRoot) {
      return parent.host
    }
    return parent
  }

  let current = getParent(element)
  while (current instanceof HTMLElement) {
    const { overflowY } = getComputedStyle(current)
    if (
      /(auto|scroll|overlay)/.test(overflowY) &&
      current.scrollHeight > current.clientHeight
    ) {
      return current
    }
    current = getParent(current)
  }
  return null
}

/**
 * Reveals an element by scrolling only its nearest scrollable ancestor, without
 * bubbling the scroll to outer ancestors. Unlike `Element.scrollIntoView`, which
 * scrolls every scrollable ancestor up to the window, this keeps outer containers
 * (e.g. a modal body or the page) still. That matters for an option deep inside a
 * dropdown: revealing it must not scroll the trigger out of the viewport, which
 * would otherwise fire the popup's viewport auto-close and snap the dropdown shut.
 */
export function scrollIntoContainer(element: HTMLElement): void {
  const container = getScrollParent(element)
  if (!container) {
    return
  }

  const containerRect = container.getBoundingClientRect()
  const elementRect = element.getBoundingClientRect()

  if (elementRect.top < containerRect.top) {
    container.scrollTop -= containerRect.top - elementRect.top
  } else if (elementRect.bottom > containerRect.bottom) {
    container.scrollTop += elementRect.bottom - containerRect.bottom
  }
}

export function getValidityStateObject(state: Partial<ValidityState>) {
  return {
    badInput: false,
    customError: false,
    patternMismatch: false,
    rangeOverflow: false,
    rangeUnderflow: false,
    stepMismatch: false,
    tooLong: false,
    tooShort: false,
    typeMismatch: false,
    valid: true,
    valueMissing: false,
    ...state,
  }
}

/**
 * Converts a string to kebab-case.
 *
 * @param str - The string to convert
 * @returns The kebab-cased string
 */
export function kebabCase(str: string): string {
  return str
    .trim()
    .toLowerCase()
    .replace(/\./g, '-') // Convert dots to dashes first
    .replace(/\s+/g, '-') // Convert spaces to dashes
    .replace(/[^a-z0-9-]/g, '') // Remove non-alphanumeric except dashes
    .replace(/--+/g, '-') // Collapse multiple dashes
    .replace(/^-+|-+$/g, '') // Remove leading/trailing dashes
}
